<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { TilopayClient } from '@/services/TiloPayClient'
import { LocationClient } from "@/services/LocationClient";
const tilopayClient = new TilopayClient();
const locationClient = new LocationClient();

const emit = defineEmits(['close'])

const props = defineProps({
  initialOrders: {
    type: Array,
    default: () => []
  },
  courseName: {
    type: String,
    required: true
  },
  amount: {
    type: [Number, String],
    required: true
  },
  currency: {
    type: String,
    required: true
  },
  courseId: {
    type: [Number, String],
    required: true
  },
  courseImage: {
    type: String,
    default: ''
  }


})

const paises = ref([])
const provincias = ref([])
const ciudades = ref([])

const paisSeleccionado = ref('')
const provinciaSeleccionada = ref('')
const ciudadSeleccionada = ref('')

// User form
const userForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  phone: ''
})

const processingPayment = ref(false)

watch(paisSeleccionado, (nuevoPais) => {
  userForm.value.country = nuevoPais;
  console.log('País seleccionado:', nuevoPais);
});

watch(provinciaSeleccionada, (nuevaProvincia) => {
  userForm.value.state = nuevaProvincia;
  console.log('Provincia seleccionada:', nuevaProvincia);
});

watch(ciudadSeleccionada, (nuevaCiudad) => {
  userForm.value.city = nuevaCiudad;
  console.log('Ciudad seleccionada:', nuevaCiudad);
});

const formValid = computed(() => {
  return (
    userForm.value.firstName &&
    userForm.value.lastName &&
    userForm.value.email &&
    userForm.value.address &&
    userForm.value.city &&
    userForm.value.state &&
    userForm.value.zipCode &&
    userForm.value.country &&
    userForm.value.phone
  )
})

const closeModal = () => {
  emit('close')
}

const processPayment = async () => {
  if (!formValid.value) return;

  processingPayment.value = true;

  try {
    // Determine currency code based on the currency prop (0 = CRC, 1 = USD)
    const currencyCode = props.currency === 0 ? 'CRC' : 'USD';

    // Format amount as string with proper decimal places
    const paymentAmount = currencyCode === 'CRC'
      ? Math.round(parseFloat(props.amount)).toString() // CRC typically doesn't use decimals
      : parseFloat(props.amount).toFixed(2); // USD uses 2 decimal places

    // Format state with country prefix (e.g., "CR-SJ")
    const formatState = (stateCode, countryCode) => {
      return `${countryCode}-${stateCode}`;
    };


    const paymentData = {
      amount: paymentAmount,
      currency: currencyCode,

      // Billing information
      billToFirstName: userForm.value.firstName,
      billToLastName: userForm.value.lastName,
      billToAddress: userForm.value.address,
      billToAddress2: userForm.value.address2 || '',
      billToCity: userForm.value.city,
      billToState: userForm.value.state,
      billToZipPostCode: userForm.value.zipCode,
      billToCountry: userForm.value.country,
      billToTelephone: userForm.value.phone.replace(/\D/g, ''), // Remove non-numeric characters
      billToEmail: userForm.value.email,

      // Shipping information (same as billing in this case)
      shipToFirstName: userForm.value.firstName,
      shipToLastName: userForm.value.lastName,
      shipToAddress: userForm.value.address,
      shipToAddress2: userForm.value.address2 || '',
      shipToCity: userForm.value.city,
      shipToState: userForm.value.state,
      shipToZipPostCode: userForm.value.zipCode,
      shipToCountry: userForm.value.country,
      shipToTelephone: userForm.value.phone.replace(/\D/g, ''),

      // Required course ID
      courseId: Number(props.courseId)
    }

    const response = await tilopayClient.createPayment(paymentData);


    if (response.url) {

      window.location.href = response.url;

    } else {
      throw new Error('No redirect URL received');
    }
  } catch (error) {
    console.error('Payment error:', error);
  } finally {
    processingPayment.value = false;
  }
};

const cargarPaises = async () => {
  paises.value = await locationClient.getPaises();
};

const cargarProvincias = async () => {
  provincias.value = [];
  ciudades.value = [];
  provincias.value = await locationClient.getProvincias(paisSeleccionado.value);
};

const cargarCiudades = async () => {
  ciudades.value = [];
  ciudades.value = await locationClient.getCiudades(paisSeleccionado.value, provinciaSeleccionada.value);
};

onMounted(() => {
  cargarPaises()
})

</script>

<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content card py-2 py-md-4 px-2 px-md-5">
      <div class="modal-header px-0 pt-0 pb-0 pb-md-3 border-bottom">
        <h5 class="ps-3 ps-md-0 fw-bold text-primary text-sm mb-0">Confirmar Compra</h5>
        <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
      </div>

      <div class="row d-flex justify-content-between mt-3">
        <!-- Right column: My Orders and User Form -->
        <div class="col-lg-6 col-md-6 col-12 pe-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white py-1 py-md-2 border-bottom">
              <h6 class="mb-0 fw-semibold text-primary"><i class="fas fa-user-circle me-2"></i>Información de
                Facturación</h6>
            </div>
            <div class="card-body px-3 px-md-4 pt-2 pt-md-4 pb-2">
              <!-- User Information Form -->
              <div class="row mb-2 mb-md-3">
                <div class="col-md-6 col-6">
                  <label class="form-label small text-muted fw-semibold">Nombre</label>
                  <input v-model="userForm.firstName" type="text" class="form-control form-control-sm rounded-3"
                    required>
                </div>
                <div class="col-md-6 col-6">
                  <label class="form-label small text-muted fw-semibold">Apellidos</label>
                  <input v-model="userForm.lastName" type="text" class="form-control form-control-sm rounded-3"
                    required>
                </div>
              </div>

              <div class="row mb-2 mb-md-3">

              <div class="col-md-12 col-6">
                <div class="mb-2 mb-md-3">

                  <label class="form-label small text-muted fw-semibold">Correo Electrónico</label>
                  <input v-model="userForm.email" type="email" class="form-control form-control-sm rounded-3" required>
                </div>
              </div>
              <div class="col-md-12 col-6">
                <div class="mb-2 mb-md-3">
                  <label class="form-label small text-muted fw-semibold">Teléfono</label>
                  <input v-model="userForm.phone" type="tel" class="form-control form-control-sm rounded-3" required>
                </div>  
              </div>
            </div>

              <div class="row mb-2 mb-md-3">
                <div class="col-md-6 col-6">
                  <div class="mb-2 mb-md-3">
                    <label class="form-label small text-muted fw-semibold">Dirección</label>
                    <input v-model="userForm.address" type="text" class="form-control form-control-sm rounded-3" required>
                  </div>          
                </div>
                <div class="col-md-6 col-6">        
                  <div class="mb-2 mb-md-3">
                    <label class="form-label small text-muted fw-semibold">Dirección 2 (Opcional)</label>
                    <input v-model="userForm.address2" type="text" class="form-control form-control-sm rounded-3">
                  </div>
                </div>
              </div>
              <div class="row mb-2 mb-md-3">
                <div class="col-md-6 col-6">
                  <label class="form-label small text-muted fw-semibold">País</label>
                  <select v-model="paisSeleccionado"
                    @change="() => { cargarProvincias(); userForm.country = paisSeleccionado }"
                    class="form-select form-select-sm rounded-3" required>
                    <option disabled value="">Selecciona un país</option>
                    <option v-for="pais in paises" :key="pais.isoCode" :value="pais.isoCode">
                      {{ pais.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 col-6" v-if="provincias.length > 0">
                  <label class="form-label small text-muted fw-semibold">Estado/Provincia</label>
                  <select v-model="provinciaSeleccionada"
                    @change="() => { cargarCiudades(); userForm.state = provinciaSeleccionada }"
                    class="form-select form-select-sm rounded-3" required>
                    <option disabled value="">Selecciona una provincia</option>
                    <option v-for="prov in provincias" :key="prov.isoCode" :value="prov.isoCode">
                      {{ prov.name.replace(/ Province$/i, '') }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="row mb-2 mb-md-3">
                <div class="col-md-6 col-6" v-if="ciudades.length > 0">
                  <label class="form-label small text-muted fw-semibold">Ciudad</label>
                  <select v-model="ciudadSeleccionada" @change="() => { userForm.city = ciudadSeleccionada }"
                    class="form-select form-select-sm rounded-3" required>
                    <option disabled value="">Selecciona una ciudad</option>
                    <option v-for="ciudad in ciudades" :key="ciudad.name" :value="ciudad.name">
                      {{ ciudad.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 col-6">
                  <label class="form-label small text-muted fw-semibold">Código Postal</label>
                  <input v-model="userForm.zipCode" type="text" class="form-control form-control-sm rounded-3" required>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Left column: Payment Methods -->
        <div class="col-lg-6 col-md-6 col-12 ps-lg-4 mt-md-0 mt-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white py-2 border-bottom">
              <h6 class="mb-0 fw-semibold text-primary"><i class="fas fa-receipt me-2"></i>Resumen de compra</h6>
            </div>
            <div class="card-body p-2 d-flex flex-column">
              <div class="mb-3 p-2 p-md-3 border rounded bg-light">
                <div class="row d-flex align-items-center mx-0 my-0">
                  <div class="col-5 col-md-12 ps-0">
                    <img :src="courseImage" class="w-100 border-radius-md" alt="Course Image"
                      style="max-height: 200px;">
                  </div>
                  <div class="col-7 col-md-12 mt-0 mt-md-3">
                    <strong class="text-dark">{{ courseName }}</strong>
                  </div>         

                </div>

                <!-- Benefits List -->
                <div class="mt-3 pt-1 pt-md-3 border-top">
                  <h6 class="fw-semibold mb-1 text-sm text-md-md">Beneficios incluidos:</h6>
                  <ul class="list-unstyled mb-0">
                    <li class="mb-2 d-flex align-items-start">
                      <i class="fas fa-check-circle text-success mt-1 me-2"></i>
                      <span class="text-sm text-md-md">Acceso completo a todo el contenido del curso</span>
                    </li>
                    <li class="mb-2 d-flex align-items-start">
                      <i class="fas fa-check-circle text-success mt-1 me-2"></i>
                      <span class="text-sm text-md-md">Certificado de finalización</span>
                    </li>

                  </ul>
                </div>
              </div>

              <div class="p-2 bg-primary bg-opacity-10 rounded text-right font-weight-bold mt-auto">
                <div class="row align-items-center">
                  <div class="col-4">
                    <strong class="text-light ms-2">Total a Pagar:</strong>
                  </div>
                  <div class="col-8 d-flex justify-content-end">
                    <h5 class="mb-0 me-2 text-light">{{ currency === 0 ? '₡' : '$' }} {{ amount }}</h5>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-center align-items-center ">
                <button class="btn btn-primary btn-block mt-3 px-6 py-3 rounded-pill fw-semibold"
                  @click="processPayment" :disabled="processingPayment || !formValid">
                  <i class="fas fa-check-circle me-2"></i>
                  {{ processingPayment ? 'Procesando...' : 'Confirmar Compra' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 1100px;
  max-height: 770px;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.3s ease-out;

}

@media (max-width: 768px) {
  .modal-content {
    max-height: 98vh;
    width: 97%;
    overflow-y: auto;

  }

}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
  letter-spacing: 0.3px;
}

.form-control,
.form-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: none;
}

.form-control:focus,
.form-select:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
}

.btn-gradient {
  background: linear-gradient(135deg, #4a90e2, #8e44ad);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.3);
}

.btn-gradient:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
}

.btn-gradient:disabled {
  background: linear-gradient(135deg, #cccccc, #999999);
  transform: none;
  box-shadow: none;
}

.card {
  border-radius: 12px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
</style>